/**
 * SendAudioGiftPage - Create personalized meditation sessions as gifts
 *
 * Flow:
 * 1. Select a biography from the library (recipient)
 * 2. Select category and fill questionnaire
 * 3. Choose voice and background music
 * 4. Generate and share the gift session
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAudioGift, type ExpressGiftData } from '@/hooks/useAudioGift';
import ExpressGiftForm from '@/components/gift/ExpressGiftForm';
import { useBiographyLibrary, type UserBiography } from '@/hooks/useBiographyLibrary';
import { useAccessibleCategories } from '@/hooks/useAccessibleCategories';
import {
  Gift,
  User,
  ArrowLeft,
  ArrowRight,
  Check,
  Copy,
  Loader2,
  Music,
  Sparkles,
  Volume2,
  Play,
  Pause,
  AlertTriangle,
  RefreshCw,
  Mail,
  Send,
  Eye,
  Edit,
  FileText,
  Wand2,
  Bolt,
  BookUser,
} from 'lucide-react';
import EnhancedAudioPlayer from '@/components/EnhancedAudioPlayer';
import { useBackgroundMusicOptions } from '@/hooks/useBackgroundMusic';
import { apiClient } from '@/utils/apiClient';
import { useAudioPreview } from '@/hooks/useAudioPreview';
import { SessionCostPreview } from '@/components/session/SessionCostPreview';

// Helper to get preview URL (uses smaller 15-second preview files)
const getBackgroundMusicPreviewUrl = (filename: string): string => {
  const baseUrl = import.meta.env.VITE_BACKEND_URL || '/api/v1';
  return `${baseUrl}/background-music/preview/${filename}`;
};
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { categoryQuestions, type QuestionConfig } from '@/components/questionnaire';
import { useVoices } from '@/hooks/useVoices';
import { useVoicePreview } from '@/hooks/useVoicePreview';
import {
  CUSTOM_TEXT_MAX_LENGTH,
  CUSTOM_TEXT_INSTRUCTIONS_MAX_LENGTH,
  type CustomTextMode,
  validateCustomText,
  validateCustomTextInstructions,
  estimateWordCount,
  estimateDuration,
} from '@/constants/customText';

type Step = 'mode' | 'biography' | 'category' | 'questionnaire' | 'customize' | 'generating' | 'error' | 'success';
type GiftMode = 'express' | 'library';

// Map occasions to default categories for express mode
const getDefaultCategoryFromOccasion = (occasion: string): string => {
  const mapping: Record<string, string> = {
    'birthday': 'life_celebrations',
    'graduation': 'life_celebrations',
    'just-because': 'self_love',
    'stressful-time': 'anxiety',
    'get-well': 'meditation',
    'thank-you': 'meditation',
    'encouragement': 'motivation',
    'new-job': 'motivation',
    'breakup': 'self_love',
    'loss': 'be_still',
    'other': 'self_love',
  };
  return mapping[occasion] || 'self_love';
};

interface SendAudioGiftPageProps {
  embedded?: boolean;
}

const SendAudioGiftPage = ({ embedded = false }: SendAudioGiftPageProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { createGiftSession, sendGiftEmail, getGiftEmailPreview } = useAudioGift();
  const { biographies = [], isLoading: loadingBiographies } = useBiographyLibrary();
  const { accessibleCategories: categories = [], loading: loadingCategories } = useAccessibleCategories();
  const { options: musicOptions, isLoading: loadingMusic } = useBackgroundMusicOptions();
  const { isPlaying, isLoading: isLoadingPreview, currentPreview, playPreview, stopPreview } = useAudioPreview();
  const { getVoicesByGender, isLoading: voicesLoading } = useVoices();
  const { previewingVoice, loadingVoice, playVoicePreview, stopVoicePreview } = useVoicePreview();

  const [step, setStep] = useState<Step>('mode');
  const [giftMode, setGiftMode] = useState<GiftMode | null>(null);
  const [selectedBiography, setSelectedBiography] = useState<UserBiography | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Express gift mode state
  const [expressGiftData, setExpressGiftData] = useState<ExpressGiftData>({
    recipientName: '',
    occasion: '',
  });
  const [shareLink, setShareLink] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Gift audio preview and email states
  const [giftAudioUrl, setGiftAudioUrl] = useState<string>('');
  const [giftSessionId, setGiftSessionId] = useState<string>('');
  const [emailToSend, setEmailToSend] = useState<string>('');
  const [emailSent, setEmailSent] = useState<boolean>(false);

  // Email preview and editing states
  const [editableGiftMessage, setEditableGiftMessage] = useState<string>('');
  const [emailPreviewHtml, setEmailPreviewHtml] = useState<string>('');
  const [showEmailPreview, setShowEmailPreview] = useState<boolean>(false);

  // Premix generation state (for when user copies link or sends email)
  const [isRegeneratingPremix, setIsRegeneratingPremix] = useState<boolean>(false);

  // Form data
  const [formData, setFormData] = useState({
    title: '',
    senderName: '',
    giftMessage: '',
    recipientEmail: '',
    sessionLength: 600, // 10 minutes in seconds
    voiceGender: 'female',
    specific_voice_id: 'female_default', // Default to female_default
    backgroundMusic: '',
  });

  // Questionnaire data for category-specific personalization
  const [questionnaireData, setQuestionnaireData] = useState<Record<string, string>>({});

  // Credit validation state
  const [canAffordSession, setCanAffordSession] = useState(true);

  // Custom text mode - flexible text integration options (CustomTextMode imported from constants)
  const [useCustomText, setUseCustomText] = useState(false);
  const [customTextMode, setCustomTextMode] = useState<CustomTextMode>('verbatim');
  const [customText, setCustomText] = useState('');
  const [customTextInstructions, setCustomTextInstructions] = useState('');

  // Stop audio previews when leaving customize step
  useEffect(() => {
    if (step !== 'customize') {
      stopPreview();
      stopVoicePreview();
    }
  }, [step, stopPreview, stopVoicePreview]);

  // Check for pre-selected biography from URL
  useEffect(() => {
    const biographyId = searchParams.get('biographyId');
    if (biographyId && biographies.length > 0) {
      const bio = biographies.find(b => b.id === biographyId);
      if (bio) {
        setSelectedBiography(bio);
        setStep('category');
      }
    }
  }, [searchParams, biographies]);

  const handleSelectBiography = (biography: UserBiography) => {
    setSelectedBiography(biography);
    setStep('category');
  };

  const handleSelectCategory = (categoryId: string) => {
    setSelectedCategory(categoryId);
    const category = categories.find(c => c.id === categoryId);
    if (category) {
      setFormData(prev => ({
        ...prev,
        title: `A Gift of ${category.name} for ${selectedBiography?.first_name || 'You'}`,
      }));
    }
    // Reset questionnaire data when category changes
    // For zen-af and news-meditation, initialize explicitLanguageLevel with the config default
    // This ensures the slider's displayed value matches what gets sent to the backend
    const key = categoryId;
    const categoryConfig = categoryQuestions[key];
    const giftQs = categoryConfig?.giftQuestions;
    const explicitDefault = giftQs?.explicitLanguageLevel?.defaultValue;

    if ((categoryId === 'zen-af' || categoryId === 'news-meditation') && explicitDefault) {
      setQuestionnaireData({ explicitLanguageLevel: String(explicitDefault) });
    } else {
      setQuestionnaireData({});
    }
    setStep('questionnaire');
  };

  // Get the gift questions for the selected category
  const getGiftQuestions = (): Record<string, QuestionConfig> | null => {
    if (!selectedCategory) return null;

    // Map category IDs to questionnaire keys
    const categoryKeyMap: Record<string, string> = {
      'sleep': 'sleep',
      'meditation': 'meditation',
      'anxiety': 'anxiety',
      'massage': 'massage',
      'motivation': 'motivation',
      'memory': 'memory',
      'be_still': 'be_still',
      'self_love': 'self_love',
      'fitness': 'fitness',
      'life_celebrations': 'life_celebrations',
      'news-meditation': 'news-meditation',
      'yoga-meditation': 'yoga-meditation',
      'zen-af': 'zen-af',
      'addiction-recovery-men': 'addiction-recovery-men',
      'couples-connection': 'couples-connection',
      'relationship-decisions': 'relationship-decisions',
      'sexual-wellness-women': 'sexual-wellness-women',
      'mens-empowerment': 'mens-empowerment',
      'womens-empowerment': 'womens-empowerment',
    };

    const key = categoryKeyMap[selectedCategory] || selectedCategory;
    const questions = categoryQuestions[key];
    return questions?.giftQuestions || null;
  };

  const giftQuestions = getGiftQuestions();
  const giftQuestionsTitle = selectedCategory && categoryQuestions[selectedCategory]?.giftTitle;

  // Check if a conditional field should be shown
  const shouldShowField = (config: QuestionConfig): boolean => {
    if (!config.conditional) return true;
    return questionnaireData[config.conditional.field] === config.conditional.value;
  };

  // Handle questionnaire field change
  const handleQuestionnaireChange = (field: string, value: string) => {
    setQuestionnaireData(prev => ({ ...prev, [field]: value }));
  };

  // Proceed from questionnaire to customize
  const handleQuestionnaireNext = () => {
    // Ensure default values for explicit language categories
    // Use the default from the category config (zen-af: 8, news-meditation: 5)
    if (selectedCategory === 'zen-af' || selectedCategory === 'news-meditation') {
      const explicitLevelConfig = giftQuestions?.explicitLanguageLevel;
      const categoryDefault = explicitLevelConfig?.defaultValue || 5;
      setQuestionnaireData(prev => ({
        ...prev,
        // Set default explicitLanguageLevel if not already set - using category config default
        explicitLanguageLevel: prev.explicitLanguageLevel || String(categoryDefault),
      }));
    }
    setStep('customize');
  };

  const handleBack = () => {
    if (step === 'biography') setStep('mode');
    else if (step === 'category') setStep('biography');
    else if (step === 'questionnaire') setStep('category');
    else if (step === 'customize') {
      // In express mode, go back to mode selection
      // In library mode, go back to questionnaire
      if (giftMode === 'express') {
        setStep('mode');
      } else {
        setStep('questionnaire');
      }
    }
  };

  const handlePreviewToggle = async (musicFile: string) => {
    try {
      const audioUrl = getBackgroundMusicPreviewUrl(musicFile);
      await playPreview(audioUrl, musicFile);
    } catch (error) {
      console.error('Failed to preview audio:', error);
      toast({
        title: 'Preview failed',
        description: 'Could not play audio preview',
        variant: 'destructive',
      });
    }
  };

  const handleGenerate = async () => {
    // Validate based on gift mode
    if (giftMode === 'library' && (!selectedBiography || !selectedCategory)) return;
    if (giftMode === 'express' && (!expressGiftData.recipientName || !expressGiftData.occasion)) return;

    // Validate custom text if enabled using shared validation functions
    if (useCustomText) {
      const textValidation = validateCustomText(customText, customTextMode);
      if (!textValidation.isValid) {
        toast({
          title: 'Custom Text Error',
          description: textValidation.error,
          variant: 'destructive',
        });
        return;
      }

      const instructionsValidation = validateCustomTextInstructions(customTextInstructions, customTextMode);
      if (!instructionsValidation.isValid) {
        toast({
          title: 'Instructions Error',
          description: instructionsValidation.error,
          variant: 'destructive',
        });
        return;
      }
    }

    setStep('generating');

    try {
      // Build params based on gift mode
      const isExpressMode = giftMode === 'express';
      const autoCategory = isExpressMode ? getDefaultCategoryFromOccasion(expressGiftData.occasion) : selectedCategory;

      const result = await createGiftSession.mutateAsync({
        // Express mode parameters
        expressMode: isExpressMode,
        expressGiftData: isExpressMode ? expressGiftData : undefined,
        // Library mode parameters (undefined for express mode)
        recipientBiographyId: isExpressMode ? undefined : selectedBiography?.id,
        recipientBiographyText: isExpressMode ? undefined : selectedBiography?.biography_text,
        recipientFirstName: isExpressMode ? expressGiftData.recipientName : selectedBiography?.first_name,
        recipientPreferredName: isExpressMode ? undefined : selectedBiography?.preferred_name,
        recipientNameUsagePreference: isExpressMode ? undefined : selectedBiography?.name_usage_preference,
        // Common parameters
        categoryId: autoCategory,
        title: formData.title,
        sessionLength: formData.sessionLength,
        voiceGender: formData.voiceGender,
        specificVoiceId: formData.specific_voice_id,
        senderName: formData.senderName,
        giftMessage: formData.giftMessage,
        recipientEmail: formData.recipientEmail || undefined,
        backgroundMusicFile: formData.backgroundMusic || undefined,
        questionnaireData: isExpressMode ? {} : questionnaireData,
        // Custom text mode - flexible text integration
        customTextMode: useCustomText ? customTextMode : undefined,
        customText: useCustomText ? customText.trim() : undefined,
        customTextInstructions: useCustomText && customTextMode === 'blend_custom' ? customTextInstructions.trim() : undefined,
      });

      setShareLink(result.shareLink);
      setGiftSessionId(result.sessionId);
      setGiftAudioUrl(result.audioUrl || '');
      setEmailToSend(formData.recipientEmail);
      setEditableGiftMessage(formData.giftMessage);
      setEmailPreviewHtml('');
      setShowEmailPreview(false);
      setEmailSent(false);
      setStep('success');
    } catch (error) {
      console.error('Gift generation failed:', error);
      // Extract user-friendly error message
      let message = 'Something went wrong while creating your gift. Please try again.';
      if (error instanceof Error) {
        if (error.message.includes('timed out')) {
          message = 'Generation is taking longer than expected. Our voice service may be experiencing high demand. Please try again in a few minutes.';
        } else if (error.message.includes('service') || error.message.includes('unavailable')) {
          message = 'Our voice service is temporarily unavailable. Please try again in a few minutes.';
        } else if (error.message) {
          message = error.message;
        }
      }
      setErrorMessage(message);
      setStep('error');
    }
  };

  const copyToClipboard = async () => {
    // Generate premix before sharing (required - blocks if fails)
    const premixReady = await ensurePremixReady();
    if (!premixReady) return;

    try {
      await navigator.clipboard.writeText(shareLink);
      toast({
        title: 'Copied!',
        description: 'Gift link copied to clipboard',
      });
    } catch (err) {
      toast({
        title: 'Failed to copy',
        description: 'Please copy the link manually',
        variant: 'destructive',
      });
    }
  };

  const handleSendGiftEmail = async () => {
    if (!emailToSend || !giftSessionId) {
      toast({
        title: 'Email required',
        description: 'Please enter an email address',
        variant: 'destructive',
      });
      return;
    }

    // Generate premix before sending (required - blocks if fails)
    const premixReady = await ensurePremixReady();
    if (!premixReady) return;

    try {
      await sendGiftEmail.mutateAsync({
        sessionId: giftSessionId,
        recipientEmail: emailToSend,
        giftMessage: editableGiftMessage || undefined,
      });
      setEmailSent(true);
    } catch (error) {
      console.error('Failed to send gift email:', error);
      // Error toast is handled by the mutation
    }
  };

  const handleLoadEmailPreview = async () => {
    if (!giftSessionId) return;

    try {
      const result = await getGiftEmailPreview.mutateAsync({
        sessionId: giftSessionId,
        recipientEmail: emailToSend,
        giftMessage: editableGiftMessage || undefined,
      });
      setEmailPreviewHtml(result.html);
      setShowEmailPreview(true);
    } catch (error) {
      console.error('Failed to load email preview:', error);
      // Error toast is handled by the mutation
    }
  };

  // Generate premix audio before sharing (voice + background music with volume settings)
  const ensurePremixReady = async (): Promise<boolean> => {
    if (!giftSessionId) return false;

    setIsRegeneratingPremix(true);
    try {
      // POST /share generates premix with current volume settings
      await apiClient.post(`/sessions/${giftSessionId}/share`, {});
      return true;
    } catch (error) {
      console.error('Failed to generate premix:', error);
      toast({
        title: 'Audio preparation failed',
        description: 'Could not prepare audio for sharing. Please try again.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsRegeneratingPremix(false);
    }
  };

  if (loadingBiographies || loadingCategories) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Get steps based on gift mode
  const getProgressSteps = () => {
    if (giftMode === 'express') {
      return ['mode', 'customize', 'success'];
    }
    return ['mode', 'biography', 'category', 'questionnaire', 'customize', 'success'];
  };

  const progressSteps = getProgressSteps();

  const content = (
    <>
      {/* Progress indicator */}
      <div className="flex justify-center mb-8">
        <div className="flex items-center gap-2">
          {progressSteps.map((s, i) => (
            <React.Fragment key={s}>
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                  step === s || progressSteps.indexOf(step) > i
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {i + 1}
              </div>
              {i < progressSteps.length - 1 && <div className="w-8 h-0.5 bg-muted" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step: Mode Selection */}
      {step === 'mode' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5" />
              How would you like to create this gift?
            </CardTitle>
            <CardDescription>
              Choose the level of personalization for your gift
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 mb-6">
              {/* Express Gift Option */}
              <div
                onClick={() => setGiftMode('express')}
                className={cn(
                  "p-5 border rounded-lg cursor-pointer transition-all",
                  giftMode === 'express'
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                    : 'border-border hover:border-primary/50'
                )}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={cn(
                    "p-2 rounded-lg",
                    giftMode === 'express' ? 'bg-primary/20' : 'bg-muted'
                  )}>
                    <Bolt className={cn(
                      "h-5 w-5",
                      giftMode === 'express' ? 'text-primary' : 'text-muted-foreground'
                    )} />
                  </div>
                  <h3 className="font-semibold text-lg">Express Gift</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Just the basics (~30 sec). Auto-selects category based on occasion.
                </p>
              </div>

              {/* Library Option */}
              <div
                onClick={() => setGiftMode('library')}
                className={cn(
                  "p-5 border rounded-lg cursor-pointer transition-all",
                  giftMode === 'library'
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                    : 'border-border hover:border-primary/50'
                )}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={cn(
                    "p-2 rounded-lg",
                    giftMode === 'library' ? 'bg-primary/20' : 'bg-muted'
                  )}>
                    <BookUser className={cn(
                      "h-5 w-5",
                      giftMode === 'library' ? 'text-primary' : 'text-muted-foreground'
                    )} />
                  </div>
                  <h3 className="font-semibold text-lg">From Library</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Full personalization (~3 min). Uses biography from your library.
                </p>
              </div>
            </div>

            {/* Express Gift Form */}
            {giftMode === 'express' && (
              <div className="mt-6 p-5 bg-muted/30 rounded-lg border border-border">
                <ExpressGiftForm data={expressGiftData} onChange={setExpressGiftData} />
              </div>
            )}

            <div className="flex justify-end mt-6">
              <Button
                onClick={() => {
                  if (giftMode === 'express') {
                    setStep('customize');
                  } else if (giftMode === 'library') {
                    setStep('biography');
                  }
                }}
                disabled={
                  !giftMode ||
                  (giftMode === 'express' && (!expressGiftData.recipientName || !expressGiftData.occasion))
                }
              >
                Next <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step: Select Biography (Library mode only) */}
      {step === 'biography' && giftMode === 'library' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Who is this gift for?
            </CardTitle>
            <CardDescription>
              Select a biography from your library. The session will be personalized for them.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {biographies.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">
                  You don't have any biographies yet. Create one first!
                </p>
                <Button onClick={() => navigate('/biographies')}>
                  Go to Biography Library
                </Button>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {biographies.filter(b => !b.is_default).map((biography) => (
                  <Card
                    key={biography.id}
                    className={cn(
                      'cursor-pointer transition-all hover:border-primary',
                      selectedBiography?.id === biography.id && 'border-primary bg-primary/5'
                    )}
                    onClick={() => handleSelectBiography(biography)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold">{biography.biography_name}</h3>
                          {biography.first_name && (
                            <p className="text-sm text-muted-foreground">
                              {biography.first_name}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {biography.biography_text.slice(0, 100)}...
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {biographies.filter(b => !b.is_default).length === 0 && biographies.length > 0 && (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">
                  Add a biography for someone you'd like to gift a session to.
                </p>
                <Button onClick={() => navigate('/biographies')}>
                  Add New Biography
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Select Category */}
      {step === 'category' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Choose a Session Type
            </CardTitle>
            <CardDescription>
              What kind of session would you like to gift to {selectedBiography?.first_name || selectedBiography?.biography_name}?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {categories.map((category) => (
                <Card
                  key={category.id}
                  className="cursor-pointer transition-all hover:border-primary"
                  onClick={() => handleSelectCategory(category.id)}
                >
                  <CardContent className="p-4 text-center">
                    <h3 className="font-semibold">{category.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {category.description?.slice(0, 60)}...
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex justify-start mt-6">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Questionnaire */}
      {step === 'questionnaire' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              {giftQuestionsTitle || 'Personalize the Session'}
            </CardTitle>
            <CardDescription>
              Help us create the perfect session for {selectedBiography?.first_name || 'your loved one'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {giftQuestions ? (
              Object.entries(giftQuestions).map(([fieldName, config]) => {
                // Skip sessionLength as we handle it in customize step
                if (fieldName === 'sessionLength') return null;
                // Skip conditional fields that shouldn't be shown
                if (!shouldShowField(config)) return null;

                return (
                  <div key={fieldName} className="space-y-2">
                    {/* Don't show label for checkbox - it's inline */}
                    {config.type !== 'checkbox' && <Label>{config.label}</Label>}

                    {config.type === 'radio' && config.options && (
                      <RadioGroup
                        value={questionnaireData[fieldName] || ''}
                        onValueChange={(value) => handleQuestionnaireChange(fieldName, value)}
                        className="space-y-2"
                      >
                        {config.options.map((option) => (
                          <div key={option} className="flex items-center space-x-2">
                            <RadioGroupItem value={option} id={`${fieldName}-${option}`} />
                            <Label htmlFor={`${fieldName}-${option}`} className="font-normal cursor-pointer">
                              {option}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    )}

                    {config.type === 'select' && config.options && (
                      <Select
                        value={questionnaireData[fieldName] || ''}
                        onValueChange={(value) => handleQuestionnaireChange(fieldName, value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                          {config.options.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {config.type === 'textarea' && (
                      <Textarea
                        value={questionnaireData[fieldName] || ''}
                        onChange={(e) => handleQuestionnaireChange(fieldName, e.target.value)}
                        placeholder={config.placeholder}
                        rows={3}
                        maxLength={config.max || config.maxLength}
                      />
                    )}

                    {config.type === 'text' && (
                      <Input
                        value={questionnaireData[fieldName] || ''}
                        onChange={(e) => handleQuestionnaireChange(fieldName, e.target.value)}
                        placeholder={config.placeholder}
                        maxLength={config.maxLength}
                      />
                    )}

                    {config.type === 'checkbox' && (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={fieldName}
                          checked={questionnaireData[fieldName] === 'true'}
                          onCheckedChange={(checked) =>
                            handleQuestionnaireChange(fieldName, checked ? 'true' : 'false')
                          }
                        />
                        <Label htmlFor={fieldName} className="font-normal cursor-pointer">
                          {config.label}
                        </Label>
                      </div>
                    )}

                    {config.type === 'slider' && (
                      <div className="p-4 bg-muted/30 rounded-lg border border-border">
                        <div className="space-y-4">
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-muted-foreground font-medium min-w-[2rem]">
                              {config.min || 1}
                            </span>
                            <Slider
                              value={[parseInt(questionnaireData[fieldName]) || config.defaultValue || 5]}
                              onValueChange={([val]) => handleQuestionnaireChange(fieldName, String(val))}
                              min={config.min || 1}
                              max={config.max || 10}
                              step={1}
                              className="flex-1"
                            />
                            <span className="text-sm text-muted-foreground font-medium min-w-[2rem] text-right">
                              {config.max || 10}
                            </span>
                          </div>
                          <div className="text-center">
                            <span className="text-2xl font-bold text-primary">
                              {parseInt(questionnaireData[fieldName]) || config.defaultValue || 5}
                            </span>
                            {fieldName === 'explicitLanguageLevel' && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {(parseInt(questionnaireData[fieldName]) || config.defaultValue || 5) <= 3
                                  ? '🟢 Mild - Minimal explicit language'
                                  : (parseInt(questionnaireData[fieldName]) || config.defaultValue || 5) <= 6
                                  ? '🟡 Moderate - Some explicit language'
                                  : '🔴 Maximum - Full explicit language'}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="text-muted-foreground text-center py-4">
                No additional questions for this category. Click Next to continue.
              </p>
            )}

            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button onClick={handleQuestionnaireNext}>
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step: Customize */}
      {step === 'customize' && (
        <Card>
          <CardHeader>
            <CardTitle>Customize Your Gift</CardTitle>
            <CardDescription>
              {giftMode === 'express'
                ? `Creating an express gift for ${expressGiftData.recipientName}`
                : `Add your personal touch to this gift for ${selectedBiography?.first_name || 'your recipient'}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Express mode summary */}
            {giftMode === 'express' && (
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Bolt className="h-4 w-4 text-primary" />
                  <span className="font-medium text-sm">Express Gift</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  For: <strong>{expressGiftData.recipientName}</strong>
                  {expressGiftData.relationship && ` (${expressGiftData.relationship})`}
                  <br />
                  Occasion: <strong>{expressGiftData.occasion.replace('-', ' ')}</strong>
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="title">Session Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="A special meditation for you"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="senderName">Your Name</Label>
              <Input
                id="senderName"
                value={formData.senderName}
                onChange={(e) => setFormData(prev => ({ ...prev, senderName: e.target.value }))}
                placeholder="From..."
              />
            </div>

            {/* Custom Text Mode Toggle */}
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg bg-gradient-to-br from-pink-50/50 to-rose-50/30">
                <div className="flex items-center gap-3">
                  {useCustomText ? (
                    <FileText className="h-5 w-5 text-pink-600" />
                  ) : (
                    <Wand2 className="h-5 w-5 text-pink-600" />
                  )}
                  <div>
                    <Label className="text-base font-medium">
                      {useCustomText ? 'Include Custom Text' : 'AI-Generated Script'}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {useCustomText
                        ? 'Add your own text to the meditation'
                        : 'AI creates a personalized script for your gift'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={useCustomText}
                  onCheckedChange={setUseCustomText}
                />
              </div>

              {/* Custom Text Mode Selection and Input */}
              {useCustomText && (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  {/* Mode Selection */}
                  <div className="space-y-2">
                    <Label>How should the AI use your text?</Label>
                    <Select value={customTextMode} onValueChange={(v) => setCustomTextMode(v as CustomTextMode)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="verbatim">
                          <div className="flex flex-col">
                            <span className="font-medium">Use Exactly (Skip AI)</span>
                            <span className="text-xs text-muted-foreground">Your text goes directly to voice synthesis</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="blend_beginning">
                          <div className="flex flex-col">
                            <span className="font-medium">Include at Beginning</span>
                            <span className="text-xs text-muted-foreground">AI starts with your text, then continues</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="blend_end">
                          <div className="flex flex-col">
                            <span className="font-medium">Include at End</span>
                            <span className="text-xs text-muted-foreground">AI generates content, ends with your text</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="blend_middle">
                          <div className="flex flex-col">
                            <span className="font-medium">Include in Middle</span>
                            <span className="text-xs text-muted-foreground">AI incorporates your text at midpoint</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="blend_woven">
                          <div className="flex flex-col">
                            <span className="font-medium">Weave Throughout</span>
                            <span className="text-xs text-muted-foreground">AI spreads your themes throughout</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="blend_custom">
                          <div className="flex flex-col">
                            <span className="font-medium">Custom Instructions</span>
                            <span className="text-xs text-muted-foreground">You tell the AI exactly how to use it</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Custom Text Input */}
                  <div className="space-y-3">
                    <Label htmlFor="custom-text">
                      {customTextMode === 'verbatim' ? 'Your Meditation Script' : 'Custom Text to Include'}
                    </Label>
                    <Textarea
                      id="custom-text"
                      value={customText}
                      onChange={(e) => setCustomText(e.target.value)}
                      placeholder={customTextMode === 'verbatim'
                        ? "Write your meditation script here. This text will be spoken exactly as written by the selected voice..."
                        : "Enter the text you want the AI to incorporate into the meditation..."}
                      rows={8}
                      maxLength={CUSTOM_TEXT_MAX_LENGTH}
                      className="font-mono text-sm"
                    />
                    {/* Word count, character count, and duration estimate */}
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex gap-4">
                        <span className="text-muted-foreground">
                          Words: <span className="font-medium text-foreground">{estimateWordCount(customText)}</span>
                        </span>
                        {customTextMode === 'verbatim' && (
                          <span className="text-muted-foreground">
                            Est. duration: <span className="font-medium text-foreground">
                              {Math.max(1, estimateDuration(customText))} min
                            </span>
                          </span>
                        )}
                      </div>
                      <span className={`text-xs ${customText.length > CUSTOM_TEXT_MAX_LENGTH * 0.9 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                        {customText.length.toLocaleString()} / {CUSTOM_TEXT_MAX_LENGTH.toLocaleString()} chars
                      </span>
                    </div>
                  </div>

                  {/* Custom Instructions (for blend_custom mode) */}
                  {customTextMode === 'blend_custom' && (
                    <div className="space-y-2">
                      <Label htmlFor="custom-text-instructions">Instructions for AI</Label>
                      <Textarea
                        id="custom-text-instructions"
                        value={customTextInstructions}
                        onChange={(e) => setCustomTextInstructions(e.target.value)}
                        placeholder="Tell the AI how to use your text. Examples: 'Use this as the opening for the first 2 minutes', 'Include this quote in the middle section', 'Repeat this affirmation 3 times throughout'..."
                        rows={3}
                        maxLength={CUSTOM_TEXT_INSTRUCTIONS_MAX_LENGTH}
                      />
                      <div className="text-xs text-muted-foreground text-right">
                        <span className={customTextInstructions.length > CUSTOM_TEXT_INSTRUCTIONS_MAX_LENGTH * 0.9 ? 'text-amber-600' : ''}>
                          {customTextInstructions.length.toLocaleString()}/{CUSTOM_TEXT_INSTRUCTIONS_MAX_LENGTH.toLocaleString()} chars
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Tips */}
                  <div className="bg-pink-50 p-3 rounded-lg text-sm space-y-2 border border-pink-200">
                    <p className="font-medium text-pink-800">
                      {customTextMode === 'verbatim' ? 'Tips for writing gift meditation scripts:' : 'Tips for custom text:'}
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-pink-700">
                      {customTextMode === 'verbatim' ? (
                        <>
                          <li>Use clear, calm language with natural pauses (add ... for longer pauses)</li>
                          <li>Write in second person ("you feel", "your breath")</li>
                          <li>Include breathing instructions and body awareness prompts</li>
                          <li>End with a gentle return to awareness</li>
                          <li>~130 words = ~1 minute of audio</li>
                        </>
                      ) : (
                        <>
                          <li>Keep your text focused on what you want included</li>
                          <li>The AI will blend your text naturally with generated content</li>
                          <li>For specific placement, use "Custom Instructions" mode</li>
                          <li>Your text doesn't need to be a complete meditation</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="giftMessage">Gift Message (Optional)</Label>
              <Textarea
                id="giftMessage"
                value={formData.giftMessage}
                onChange={(e) => setFormData(prev => ({ ...prev, giftMessage: e.target.value }))}
                placeholder="Write a heartfelt message..."
                rows={3}
              />
            </div>

            {/* Session Length */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-pink-500" />
                Session Length
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 180, label: '3 min' },
                  { value: 300, label: '5 min' },
                  { value: 600, label: '10 min' },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, sessionLength: option.value }))}
                    className={cn(
                      "p-3 rounded-lg border-2 transition-all text-center font-medium",
                      formData.sessionLength === option.value
                        ? "border-pink-500 bg-pink-100 text-pink-700"
                        : "border-gray-200 hover:border-pink-300 hover:bg-pink-50"
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Voice Selection - Gift Themed */}
            <div className="space-y-4 p-5 rounded-xl border-2 border-pink-200 bg-gradient-to-br from-pink-50/50 to-rose-50/30">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center gap-2 mb-2">
                  <Volume2 className="h-6 w-6 text-pink-600" />
                  <h3 className="text-lg font-semibold text-pink-800">Choose the Voice</h3>
                </div>
                <p className="text-sm text-pink-600">Select who will narrate this special gift</p>
              </div>

              {/* Voice Gender Selection */}
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    voiceGender: 'female',
                    specific_voice_id: 'female_default'
                  }))}
                  className={cn(
                    "p-4 rounded-xl border-2 transition-all text-center",
                    formData.voiceGender === 'female'
                      ? "border-pink-500 bg-pink-100 shadow-lg shadow-pink-200"
                      : "border-gray-200 hover:border-pink-300 hover:bg-pink-50"
                  )}
                >
                  <User className="h-8 w-8 text-pink-600 mx-auto mb-2" />
                  <span className="font-semibold text-gray-800">Female Voice</span>
                  <p className="text-xs text-gray-500 mt-1">Warm & soothing</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    voiceGender: 'male',
                    specific_voice_id: 'male_default'
                  }))}
                  className={cn(
                    "p-4 rounded-xl border-2 transition-all text-center",
                    formData.voiceGender === 'male'
                      ? "border-pink-500 bg-pink-100 shadow-lg shadow-pink-200"
                      : "border-gray-200 hover:border-pink-300 hover:bg-pink-50"
                  )}
                >
                  <User className="h-8 w-8 text-pink-600 mx-auto mb-2" />
                  <span className="font-semibold text-gray-800">Male Voice</span>
                  <p className="text-xs text-gray-500 mt-1">Calm & grounding</p>
                </button>
              </div>

              {/* Specific Voice Selection with Preview */}
              {formData.voiceGender && (
                <div className="mt-5 pt-5 border-t border-pink-200">
                  <p className="text-sm font-medium text-pink-700 mb-3">
                    Choose {formData.voiceGender === 'male' ? 'Male' : 'Female'} Voice Style
                  </p>
                  {voicesLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-pink-500" />
                      <span className="ml-2 text-sm text-pink-600">Loading voices...</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {getVoicesByGender(formData.voiceGender).map((voice) => (
                        <div
                          key={voice.id}
                          className={cn(
                            "flex items-center justify-between p-3 rounded-lg border-2 transition-all cursor-pointer",
                            formData.specific_voice_id === voice.id
                              ? "border-pink-500 bg-pink-100"
                              : "border-gray-200 hover:border-pink-300 hover:bg-pink-50"
                          )}
                          onClick={() => setFormData(prev => ({ ...prev, specific_voice_id: voice.id }))}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <div className={cn(
                                "w-3 h-3 rounded-full border-2",
                                formData.specific_voice_id === voice.id
                                  ? "border-pink-500 bg-pink-500"
                                  : "border-gray-300"
                              )} />
                              <span className="font-medium text-gray-800">{voice.name}</span>
                            </div>
                            <p className="text-xs text-gray-500 ml-5 mt-1 truncate">{voice.description}</p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              playVoicePreview(voice.id);
                            }}
                            disabled={loadingVoice === voice.id}
                            className="ml-2 p-2 hover:bg-pink-200"
                            title={previewingVoice === voice.id ? "Stop preview" : "Preview voice"}
                          >
                            {loadingVoice === voice.id ? (
                              <Loader2 className="h-4 w-4 text-pink-600 animate-spin" />
                            ) : previewingVoice === voice.id ? (
                              <Pause className="h-4 w-4 text-pink-600" />
                            ) : (
                              <Play className="h-4 w-4 text-pink-600" />
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Credit Cost Preview */}
            <SessionCostPreview
              durationMinutes={formData.sessionLength / 60}
              onCostCalculated={(canAfford) => setCanAffordSession(canAfford)}
            />

            {/* Background Music Selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Volume2 className="h-4 w-4" />
                Background Music (Optional)
              </Label>
              <Select
                value={formData.backgroundMusic || 'none'}
                onValueChange={(value) => setFormData(prev => ({ ...prev, backgroundMusic: value === 'none' ? '' : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select background music" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="none">
                    <div className="flex flex-col">
                      <span className="font-medium">No background music</span>
                      <span className="text-xs text-muted-foreground">Voice only - no music</span>
                    </div>
                  </SelectItem>
                  {loadingMusic ? (
                    <SelectItem value="loading" disabled>
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading music options...
                      </div>
                    </SelectItem>
                  ) : (
                    musicOptions.map((music) => (
                      <SelectItem key={music.filename} value={music.filename}>
                        <div className="flex flex-col">
                          <span className="font-medium">{music.name}</span>
                          <span className="text-xs text-muted-foreground">{music.description}</span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>

              {/* Visual preview of selected music with audio preview button */}
              {formData.backgroundMusic && formData.backgroundMusic !== 'none' && (
                <div className="bg-primary/5 border border-primary/20 rounded-md p-3 mt-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <Music className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-primary truncate">
                          {musicOptions.find(m => m.filename === formData.backgroundMusic)?.name || formData.backgroundMusic}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {musicOptions.find(m => m.filename === formData.backgroundMusic)?.description || 'Background music will play softly during the session'}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handlePreviewToggle(formData.backgroundMusic)}
                      disabled={currentPreview === formData.backgroundMusic && isLoadingPreview}
                      className="flex-shrink-0"
                    >
                      {currentPreview === formData.backgroundMusic && isLoadingPreview ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          Loading
                        </>
                      ) : currentPreview === formData.backgroundMusic && isPlaying ? (
                        <>
                          <Pause className="h-4 w-4 mr-1" />
                          Stop
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-1" />
                          Preview
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="recipientEmail">Recipient Email (Optional)</Label>
              <Input
                id="recipientEmail"
                type="email"
                value={formData.recipientEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, recipientEmail: e.target.value }))}
                placeholder="They'll receive an email with the link"
              />
            </div>

            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={!formData.title || !formData.senderName || !canAffordSession || (useCustomText && !customText.trim())}
              >
                Generate Gift
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Generating */}
      {step === 'generating' && (
        <Card>
          <CardContent className="py-16 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Creating Your Gift...</h3>
            <p className="text-muted-foreground mb-4">
              We're generating a personalized meditation session for {selectedBiography?.first_name || 'your recipient'}.
            </p>
            <p className="text-sm text-muted-foreground">
              This may take up to 5 minutes. Please don't close this page.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {step === 'error' && (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="flex justify-center mb-6">
              <div className="rounded-full bg-red-100 dark:bg-red-900 p-4">
                <AlertTriangle className="h-10 w-10 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <h3 className="text-2xl font-bold mb-2">Generation Failed</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {errorMessage}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
              <Button onClick={handleGenerate} className="flex-1">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
              <Button variant="outline" onClick={() => setStep('customize')} className="flex-1">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Modify Settings
              </Button>
            </div>
            <Button
              variant="ghost"
              onClick={() => {
                setStep('biography');
                setSelectedBiography(null);
                setSelectedCategory('');
                setQuestionnaireData({});
              }}
              className="mt-4"
            >
              Start Over
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Success */}
      {step === 'success' && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="rounded-full bg-green-100 dark:bg-green-900 p-4">
                  <Check className="h-10 w-10 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-2">Gift Created!</h3>
              <p className="text-muted-foreground mb-6">
                Your personalized meditation gift is ready to share.
              </p>
            </div>

            <div className="max-w-md mx-auto space-y-6">
              {/* Audio Preview with Volume Control */}
              {giftAudioUrl && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Preview Your Gift</Label>
                  <EnhancedAudioPlayer
                    audioUrl={giftAudioUrl}
                    sessionTitle={formData.title}
                    sessionId={giftSessionId}
                    backgroundAudioUrl={
                      formData.backgroundMusic
                        ? `${import.meta.env.VITE_BACKEND_URL || '/api/v1'}/background-music/files/${formData.backgroundMusic}`
                        : undefined
                    }
                  />
                </div>
              )}

              {/* Share Link */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Gift Link</Label>
                <div className="flex gap-2">
                  <Input value={shareLink} readOnly className="text-sm" />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={copyToClipboard}
                    disabled={isRegeneratingPremix}
                  >
                    {isRegeneratingPremix ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {formData.backgroundMusic && (
                  <p className="text-xs text-muted-foreground">
                    💡 The background music volume you set above will be locked when you copy/share this link.
                    Adjust volume first, then share.
                  </p>
                )}
              </div>

              {/* Email Section */}
              <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                {emailSent ? (
                  <div className="text-center space-y-2">
                    <div className="flex justify-center">
                      <div className="rounded-full bg-green-100 dark:bg-green-900 p-2">
                        <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                      </div>
                    </div>
                    <p className="text-sm font-medium text-green-600 dark:text-green-400">
                      Email sent to {emailToSend}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEmailSent(false)}
                    >
                      Send to another email
                    </Button>
                  </div>
                ) : (
                  <>
                    <Label className="text-sm font-medium">Send via Email</Label>

                    {/* Recipient Email */}
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Recipient Email</Label>
                      <Input
                        type="email"
                        placeholder="recipient@email.com"
                        value={emailToSend}
                        onChange={(e) => {
                          setEmailToSend(e.target.value);
                          setShowEmailPreview(false);
                        }}
                      />
                    </div>

                    {/* Editable Gift Message */}
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Personal Message (optional)</Label>
                      <Textarea
                        placeholder="Add a personal message to your gift..."
                        value={editableGiftMessage}
                        onChange={(e) => {
                          setEditableGiftMessage(e.target.value);
                          setShowEmailPreview(false); // Hide preview when message changes
                        }}
                        rows={3}
                        className="resize-none"
                      />
                    </div>

                    {/* Preview and Send Buttons */}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={handleLoadEmailPreview}
                        disabled={getGiftEmailPreview.isPending}
                        className="flex-1"
                      >
                        {getGiftEmailPreview.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Eye className="h-4 w-4 mr-2" />
                            Preview Email
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={handleSendGiftEmail}
                        disabled={!emailToSend || sendGiftEmail.isPending || isRegeneratingPremix}
                        className="flex-1"
                      >
                        {sendGiftEmail.isPending || isRegeneratingPremix ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Send
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Email Preview */}
                    {showEmailPreview && emailPreviewHtml && (
                      <div className="mt-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">Email Preview</Label>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowEmailPreview(false)}
                          >
                            Hide
                          </Button>
                        </div>
                        <div className="border rounded-lg overflow-hidden bg-white">
                          <iframe
                            srcDoc={emailPreviewHtml}
                            className="w-full h-[400px] border-0"
                            title="Email Preview"
                            sandbox="allow-same-origin"
                          />
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground">
                      We'll send an email with the gift link to your recipient
                    </p>
                  </>
                )}
              </div>

              {/* Action buttons */}
              <div className="space-y-2 pt-2">
                <Button variant="outline" className="w-full" onClick={() => navigate('/session-history')}>
                  View in Session History
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    // Reset all state to start fresh
                    setStep('biography');
                    setSelectedBiography(null);
                    setSelectedCategory('');
                    setQuestionnaireData({});
                    setFormData({
                      title: '',
                      senderName: '',
                      giftMessage: '',
                      recipientEmail: '',
                      sessionLength: 600,
                      voiceGender: 'female',
                      specific_voice_id: 'female_default',
                      backgroundMusic: '',
                    });
                    setShareLink('');
                    setGiftAudioUrl('');
                    setGiftSessionId('');
                    setEmailToSend('');
                    setEditableGiftMessage('');
                    setEmailPreviewHtml('');
                    setShowEmailPreview(false);
                    setEmailSent(false);
                    setCanAffordSession(true);
                    setIsRegeneratingPremix(false);
                    // Reset custom text mode
                    setUseCustomText(false);
                    setCustomTextMode('verbatim');
                    setCustomText('');
                    setCustomTextInstructions('');
                  }}
                >
                  <Gift className="h-4 w-4 mr-2" />
                  Send Another Gift
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );

  // When embedded, return just the content without container/header
  if (embedded) {
    return content;
  }

  // Standalone mode with container and header
  return (
    <div className="container max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Gift className="h-10 w-10 text-pink-500" />
          <h1 className="text-4xl font-bold">Send an Audio Gift</h1>
        </div>
        <p className="text-muted-foreground text-lg">
          Create a personalized meditation session as a gift for someone special
        </p>
      </div>

      {content}
    </div>
  );
};

export default SendAudioGiftPage;
